import { logger } from '@/lib/logger';
import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database.generated';
import { getAuthToken, getCurrentUserId } from './auth-token';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY_PROD;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Supabase client — used as a typed PostgREST query builder.
// Authentication is handled by Firebase; the Supabase auth subsystem is disabled.
// .from() and .rpc() calls are PostgREST REST calls with Firebase JWT injection.
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      fetch: customFetch,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    db: {
      schema: 'public'
    }
  }
);

/**
 * Custom fetch that injects Firebase auth token and provides timeout protection.
 *
 * - Injects `Authorization: Bearer <firebase_token>` on all REST calls
 * - If user is not authenticated, removes Authorization header (PostgREST falls back to db-anon-role)
 * - Skips custom handling for WebSocket/realtime operations
 * - Provides variable timeouts: 10s default, 60s uploads
 */
async function customFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const urlString = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.toString()
      : input instanceof Request
        ? input.url
        : '';

  // Skip custom fetch for WebSocket/realtime operations
  if (urlString.includes('/realtime') ||
      (init?.headers && (init.headers as any)['Upgrade'] === 'websocket')) {
    return fetch(input, init);
  }

  const headers = new Headers(init?.headers);

  // Inject Firebase auth token (replaces Supabase's auto-injected auth header)
  try {
    const token = await getAuthToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    } else {
      // No auth — remove Authorization header so PostgREST uses db-anon-role
      headers.delete('Authorization');
    }
  } catch {
    // Token fetch failed — proceed without auth
    headers.delete('Authorization');
  }

  // Remove apikey header — PostgREST doesn't need it
  headers.delete('apikey');

  // Ensure Accept header is always set to prevent 406 errors
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (!headers.has('Content-Type') && init?.method !== 'GET' && init?.method !== 'HEAD') {
    headers.set('Content-Type', 'application/json');
  }

  const controller = new AbortController();
  const startTime = Date.now();
  const requestId = Math.random().toString(36).substring(2, 9);

  // Extract URL path for logging
  let url: string;
  try {
    url = new URL(urlString, window.location.origin).pathname;
  } catch {
    url = 'Request';
  }

  logger.debug(`[${requestId}] Starting request: ${url}`);

  // Check network status
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    logger.warn(`[${requestId}] Network appears to be offline`);
    if (url.includes('/subjects') || url.includes('/collections') || url.includes('/flashcards')) {
      return Promise.reject(new Error('Network connection unavailable. Please check your internet connection.'));
    }
  }

  // Variable timeouts based on request type
  const isFileUpload = url.includes('/storage/') || url.includes('/analyze') || (init?.method === 'POST' && init?.body instanceof FormData);

  let timeoutDuration = 10000; // Default 10 seconds
  if (isFileUpload) timeoutDuration = 60000;

  const timeoutId = setTimeout(() => {
    controller.abort();
    const duration = Date.now() - startTime;
    logger.warn(`[${requestId}] Request timed out after ${timeoutDuration / 1000}s: ${url} (${duration}ms)`);
  }, timeoutDuration);

  return fetch(input, { ...init, headers, signal: controller.signal })
    .then(response => {
      const duration = Date.now() - startTime;
      logger.info(`[${requestId}] Completed: ${url} (${duration}ms) - Status: ${response.status}`);
      return response;
    })
    .catch(error => {
      const duration = Date.now() - startTime;
      logger.error(`[${requestId}] Failed: ${url} (${duration}ms)`, error);

      if (error.name === 'AbortError') {
        throw new Error(`Request to ${url} timed out after ${timeoutDuration / 1000} seconds. Please check your network connection.`);
      }

      if (error.message?.includes('NetworkError') || error.message?.includes('Failed to fetch')) {
        throw new Error(`Network error when requesting ${url}. Please check your internet connection.`);
      }

      throw error;
    })
    .finally(() => {
      clearTimeout(timeoutId);
    });
}

// Enhanced error logging with context.
// Best-effort: never throws, never logs at error level (would recurse via ErrorBoundary).
export async function logError(
  error: Error | unknown,
  context: string
) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const stackTrace = error instanceof Error ? error.stack : undefined;

  try {
    const token = await getAuthToken();
    const userId = getCurrentUserId();
    if (!token || !userId) {
      // Anon users hit RLS — skip silently rather than spamming the console.
      return;
    }

    const { error: insertError } = await supabase
      .from('error_logs')
      .insert([{
        message: context ? `${context}: ${errorMessage}` : errorMessage,
        stack_trace: stackTrace,
        investigated: false,
        user_id: userId
      }]);

    if (insertError) {
      // RLS denial or schema mismatch — warn, don't error (avoid ErrorBoundary recursion).
      // Include message so a future error_logs schema change doesn't become a silent debugging dead-end.
      logger.warn('error_logs insert rejected', {
        code: insertError.code,
        message: insertError.message,
      });
    }
  } catch {
    // Swallow — error logger itself must never throw.
  }
}
